import { CreatableTagsInput, EmptyState, FlexColumn, FlexRow, Loading, useModal } from '@gorila-shared-ui/components';
import { StatefulPanel } from 'baseui/accordion';
import { Badge } from 'baseui/badge';
import { Tag } from 'baseui/tag';
import { HeadingXSmall, LabelMedium, LabelSmall, LabelXSmall, ParagraphSmall } from 'baseui/typography';
import { useState } from 'react';
import { areEqual } from 'react-window';
import { useRecoilState, useRecoilValue } from 'recoil';
import { useStyles } from '../../../hooks/useStyles';
import {
  editingNotificationSettingsState,
  loadingNotificationSettingsState,
  notificationSettingsByCategoriesState,
  notificationSettingsCountState,
} from '../../../storage/settings';
import { NotificationConfig } from '../../../types/notificationConfig';
import { formatPhone } from '../../../utils/strings';
import MultipleInternationalPhoneModal from '../../shared/MultipleInternationalPhoneModal';

type NotificationSettingProps = Readonly<{
  notificationSetting: NotificationConfig;
  onEmailsChange: (emails: string[], setting: NotificationConfig) => void;
  onPhonesChange: (phones: string[], setting: NotificationConfig) => void;
  onEditPhones: (setting: NotificationConfig) => void;
}>;

function SmsTags({
  notificationSetting,
  onPhonesChange,
  onEditPhones,
}: Omit<NotificationSettingProps, 'onEmailsChange'>) {
  const { theme, css } = useStyles();
  const { sms } = notificationSetting;

  return (
    <FlexColumn gap={theme.sizing.scale300}>
      <LabelSmall>Enviar SMS a:</LabelSmall>

      <div
        className={css({
          flex: '1 1 0%',
          flexWrap: 'wrap',
          display: 'flex',
          alignItems: 'center',
          backgroundColor: theme.colors.inputFill,
          border: `2px solid ${theme.colors.inputBorder}`,
          minHeight: '32px',
          borderRadius: theme.borders.inputBorderRadius,
          cursor: 'pointer',
        })}
        onClick={() => onEditPhones(notificationSetting)}
      >
        {sms.map((phone, index) => (
          <Tag
            kind="custom"
            color="#000"
            hierarchy="primary"
            key={index}
            onClick={(event) => {
              event.stopPropagation();
            }}
            onActionClick={() =>
              onPhonesChange(
                sms.filter((item) => item !== phone),
                notificationSetting,
              )
            }
            overrides={{
              Root: {
                style: {
                  borderTopLeftRadius: theme.sizing.scale800,
                  borderTopRightRadius: theme.sizing.scale800,
                  borderBottomLeftRadius: theme.sizing.scale800,
                  borderBottomRightRadius: theme.sizing.scale800,
                  paddingTop: theme.sizing.scale0,
                  paddingBottom: theme.sizing.scale0,
                  paddingRight: theme.sizing.scale300,
                  paddingLeft: theme.sizing.scale300,
                  marginTop: theme.sizing.scale0,
                  marginBottom: theme.sizing.scale0,
                  marginLeft: theme.sizing.scale0,
                  marginRight: theme.sizing.scale0,
                  cursor: 'default',
                },
              },
              Text: {
                style: {
                  maxWidth: 'none',
                },
              },
            }}
          >
            {formatPhone(phone)}
          </Tag>
        ))}

        <ParagraphSmall
          margin={0}
          color={theme.colors.contentTertiary}
          style={{
            padding: `${theme.sizing.scale200} ${theme.sizing.scale550}`,
          }}
        >
          ##########
        </ParagraphSmall>
      </div>
    </FlexColumn>
  );
}

function NotificationSettingItem({
  notificationSetting,
  onEmailsChange,
  onPhonesChange,
  onEditPhones,
}: NotificationSettingProps) {
  const { theme, css } = useStyles();

  return (
    <FlexColumn
      gap={theme.sizing.scale0}
      padding={theme.sizing.scale600}
      classNames={css({
        ...theme.borders.border300,
        borderRadius: theme.borders.popoverBorderRadius,
      })}
    >
      <LabelMedium
        margin={0}
        style={{
          fontWeight: theme.typography.HeadingXSmall.fontWeight,
        }}
      >
        {notificationSetting.notificationDescription}
      </LabelMedium>

      <CreatableTagsInput
        label="Enviar correo a:"
        tags={notificationSetting.email}
        onTagsChange={(newEmails) => onEmailsChange(newEmails, notificationSetting)}
        placeholder="juan@mail.com"
        caption="Presiona enter para agregar"
        noMargin
      />

      {/* TODO: Update to whatsapp tags when backend functionality is defined.
      <SmsTags
        notificationSetting={notificationSetting}
        onPhonesChange={onPhonesChange}
        onEditPhones={onEditPhones}
      /> */}
    </FlexColumn>
  );
}

export function NotificationSettingsList() {
  const { css, theme, classes, concatenateClasses } = useStyles();
  const loadingNotificationSettings = useRecoilValue(loadingNotificationSettingsState);

  const notificationSettingsByCategory = useRecoilValue(notificationSettingsByCategoriesState);
  const notificationSettingsCount = useRecoilValue(notificationSettingsCountState);
  const [notificationSettings, setNotificationSettings] = useRecoilState(editingNotificationSettingsState);
  const [selectedNotificationConfig, setSelectedNotificationConfig] = useState<NotificationConfig>();
  const { closeModal, openModal, showModal } = useModal();

  const updateNotificationSetting = (
    notificationSetting: NotificationConfig,
    update: (setting: NotificationConfig) => NotificationConfig,
  ) => {
    if (!notificationSettingsByCategory) {
      return;
    }
    const index = notificationSettings?.findIndex((setting) => areEqual(setting, notificationSetting));
    if (index === undefined || index === -1) {
      return;
    }
    setNotificationSettings((prev) => {
      const newNotificationSettings = [...prev!];
      newNotificationSettings[index] = update({
        ...newNotificationSettings[index],
      });
      return newNotificationSettings;
    });
  };

  const onEmailsChange = (newEmails: string[], notificationSetting: NotificationConfig) => {
    updateNotificationSetting(notificationSetting, (setting) => ({
      ...setting,
      email: newEmails,
    }));
  };

  const onPhonesChange = (newPhones: string[], notificationSetting: NotificationConfig) => {
    updateNotificationSetting(notificationSetting, (setting) => ({
      ...setting,
      sms: newPhones,
    }));
  };

  const handleEditPhones = (notificationSetting: NotificationConfig) => {
    setSelectedNotificationConfig(notificationSetting);
    openModal();
  };

  const handleCloseEditPhones = (newPhones?: string[]) => {
    if (newPhones && selectedNotificationConfig) {
      onPhonesChange(newPhones, selectedNotificationConfig);
    }
    closeModal();
    setTimeout(() => {
      setSelectedNotificationConfig(undefined);
    }, 500);
  };

  const renderNotificationSetting = (notificationSetting: NotificationConfig) => (
    <NotificationSettingItem
      notificationSetting={notificationSetting}
      onEmailsChange={onEmailsChange}
      onPhonesChange={onPhonesChange}
      onEditPhones={handleEditPhones}
    />
  );

  return (
    <>
      <FlexColumn
        gap={theme.sizing.scale700}
        classNames={css({
          flex: 1,
          overflow: 'auto',
          paddingRight: theme.sizing.scale600,
        })}
      >
        {!notificationSettingsByCategory && !loadingNotificationSettings && (
          <EmptyState title="No cuentas con notificaciones" />
        )}
        {!notificationSettingsByCategory && loadingNotificationSettings && <Loading />}
        {notificationSettingsByCategory && (
          <>
            <ul
              className={css({
                margin: 0,
                paddingLeft: 0,
                paddingRight: 0,
                flex: 1,
              })}
            >
              {notificationSettingsByCategory.map((data) => (
                <StatefulPanel
                  key={data.category}
                  initialState={{
                    expanded: true,
                  }}
                  title={
                    <FlexRow classNames={concatenateClasses(classes.centeredBetween, css({ width: '100%' }))}>
                      <HeadingXSmall margin={0}>{data.categoryDisplay}</HeadingXSmall>
                      <Badge
                        color="primary"
                        content={data.settings?.length ?? 0}
                      />
                    </FlexRow>
                  }
                  overrides={{
                    Content: {
                      style: {
                        paddingTop: '0',
                        paddingBottom: '16px',
                        paddingRight: '0',
                        paddingLeft: '20px',
                      },
                    },
                  }}
                >
                  <FlexColumn>
                    {data.settings?.map((notificationSetting, index) => (
                      <div key={index}>{renderNotificationSetting(notificationSetting)}</div>
                    ))}
                  </FlexColumn>
                </StatefulPanel>
              ))}
            </ul>

            {!notificationSettingsByCategory.length && (
              <EmptyState
                title="No se encontraron resultados"
                description="Intenta con otra busqueda"
              />
            )}
          </>
        )}
      </FlexColumn>
      {notificationSettingsByCategory && (
        <FlexRow>
          <LabelXSmall color={theme.colors.contentSecondary}>{notificationSettingsCount}</LabelXSmall>
        </FlexRow>
      )}
      <MultipleInternationalPhoneModal
        isOpen={showModal}
        onClose={handleCloseEditPhones}
        title={`Enviar SMS de "${selectedNotificationConfig?.notificationDescription?.toLowerCase()}" a:`}
        phoneNumbers={selectedNotificationConfig?.sms}
      />
    </>
  );
}
